import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { api } from '@/services/api';
import { User } from '@/types';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (credentials: {email: string, password: string}) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const authChannelRef = useRef<BroadcastChannel | null>(null);
  
  useEffect(() => {
    if (typeof BroadcastChannel !== 'undefined') {
      authChannelRef.current = new BroadcastChannel('auth-channel');
      
      authChannelRef.current.onmessage = (event) => {
        const { type, user: broadcastUser } = event.data;
        
        switch (type) {
          case 'LOGIN':
            if (broadcastUser) {
              setUser(broadcastUser);
              setError(null);
            }
            break;
            
          case 'LOGOUT':
            setUser(null);
            setError(null);
            break;
        }
      };
    } else {
      console.warn('BroadcastChannel not supported in this browser');
    }
    
    return () => {
      if (authChannelRef.current) {
        authChannelRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const responseData = await api.auth.getMe();
        if (responseData && responseData.data && responseData.data.currentUser) {
          setUser(responseData.data.currentUser);
        } else {
          setUser(null);
        }
      } catch (error) {
        // Only handles unexpected errors now, not 401s
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  const login = async (credentials: {email: string, password: string}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.auth.login(credentials);
      if (response && response.data && response.data.user) {
        setUser(response.data.user);
        
        if (authChannelRef.current) {
          authChannelRef.current.postMessage({
            type: 'LOGIN',
            user: response.data.user,
            timestamp: Date.now()
          });
        }
      }
    } catch (err: any) {
      setError(err.message || 'Login failed. Please check your credentials.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await api.auth.logout();
      setUser(null);
      
      if (authChannelRef.current) {
        authChannelRef.current.postMessage({
          type: 'LOGOUT',
          timestamp: Date.now()
        });
      }
    } catch (err) {
      console.error('Logout failed', err);
    }
  };

  const value = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    loading,
    error
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
